import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CreditCheckStatus } from '@app/shared/enums/credit-check-status';
import { CreditCheckDetails, CreditCheckRequest, CreditCheckStatusLabel } from '@app/shared/models/credit-check';
import { CreditCheckService } from '@app/shared/services/credit-check.service';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { creditCheckViewModelSelector, loggedInUtilityHubIdentitySelector, termOfOfferSelector, creditCheckRequestDataSelector, quoteRequestStateSelector } from '@app/state/app.selectors';
import { AppState } from '@app/state/app.state';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Store } from '@ngrx/store';
import { SetCreditCheckStatus, SetIsCreditCheckApproved, SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { LoadConsumptionData, SetUtilityInformation } from '@utility-information/state/actions/utility-information.actions';
import { Subscription, forkJoin } from 'rxjs';
import { StatusIcons } from './models/StatusIcons';
import { StatusMessages } from './models/StatusMessages';
import { QuoteRequestState } from '@app/shared/models/quote-request-state';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';

@Component({
  selector: 'app-credit-check',
  templateUrl: './credit-check.component.html',
  styleUrls: ['./credit-check.component.scss'],
})
export class CreditCheckComponent implements OnInit, OnDestroy {
  private creditCheckButton$: Subscription;
  public displayCreditCheckButton: boolean;
  public displayApproveCreditCheckButton: boolean;
  private load$: Subscription;
  private route$: Subscription;
  private quoteRequestId: number;
  private loggedInUtilityHubIdentity$: Subscription;
  public uhUser: UtilityHubIdentity;
  private performCreditCheck$: Subscription;
  public creditCheckStatus: CreditCheckStatus;
  public isCreditCheckEnabled: boolean;
  public isCreditCheckApproved: boolean = false;
  public enableApproveCreditCheckButton: boolean = true;
  private term$: Subscription;
  private term: number;
  private creditCheckRequestData$: Subscription;
  public creditCheckRequestData: CreditCheckRequest;
  public creditCheckApprovedBy: string;
  public creditCheckDetails: CreditCheckDetails;
  public formatApprovedDate: string;
  public showCreditCheckButtonData: any;
  public isCreditCheckInProgress: boolean;
  public showCreditCheckSpinner: boolean;
  public isApproveCreditCheckInProgress: boolean;
  public showApproveSpinner: boolean;
  public quoteRequestState$: Subscription;
  public quoteRequestState: QuoteRequestState;

  get CanDoCreditCheck(): boolean {
    return !this.isCreditCheckApproved && (
      this.creditCheckStatus === CreditCheckStatus.CreditInsufficientData ||
      this.creditCheckStatus === CreditCheckStatus.CreditUnknown ||
      this.creditCheckStatus === CreditCheckStatus.CreditError ||
      this.creditCheckStatus === CreditCheckStatus.CreditNotPerformed)
  }

  get isApproveButtonVisible(): boolean {
    let userIsCreditAdmin: boolean = this.uhUser?.currentOrganisation?.role === UserRole.CreditAdmin;
    return userIsCreditAdmin;
  }

  get statusLabel(): CreditCheckStatusLabel {
    let statusLabel: CreditCheckStatusLabel;

    if (this.creditCheckDetails) {
      let formattedRequestDate: string = formatDate(this.creditCheckDetails.requestDate, 'MM/dd/yyyy', 'en-US');

      switch (this.creditCheckStatus) {
        case CreditCheckStatus.CreditInsufficientData:
          statusLabel = {
            displayStatus: StatusMessages.InsufficientData(formattedRequestDate, this.creditCheckDetails.errorMessage),
            icon: StatusIcons.Warning
          }
          break;
        case CreditCheckStatus.CreditPending:
          statusLabel = {
            displayStatus: StatusMessages.Generic(formattedRequestDate, "Pending response"),
            icon: StatusIcons.Warning
          }
          break;
        case CreditCheckStatus.CreditPassed:
          statusLabel = {
            displayStatus: StatusMessages.Generic(formattedRequestDate, "Pass"),
            icon: StatusIcons.Success
          }
          break;
        case CreditCheckStatus.CreditFailed:
          statusLabel = {
            displayStatus: StatusMessages.Generic(formattedRequestDate, "Fail"),
            icon: StatusIcons.Failed
          }
          break;
        case CreditCheckStatus.CreditReviewRequired:
          statusLabel = {
            displayStatus: StatusMessages.Generic(formattedRequestDate, "Review required"),
            icon: StatusIcons.Warning
          }
          break;
        case CreditCheckStatus.CreditError:
          statusLabel = {
            displayStatus: StatusMessages.Error(this.creditCheckDetails.errorMessage),
            icon: StatusIcons.Failed
          }
          break;
        case CreditCheckStatus.CreditUnknown:
          statusLabel = {
            displayStatus: StatusMessages.Generic(formattedRequestDate, "Unknown"),
            icon: StatusIcons.Failed
          }
          break;
      }
    }
    return statusLabel;
  }

  constructor(
    private store: Store<AppState>,
    private quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private creditCheckService: CreditCheckService,
  ) { }

  ngOnDestroy(): void {
    this.route$?.unsubscribe();
    this.load$?.unsubscribe();
    this.loggedInUtilityHubIdentity$?.unsubscribe();
    this.creditCheckButton$?.unsubscribe();

    this.performCreditCheck$?.unsubscribe();
    this.term$?.unsubscribe();
    this.creditCheckRequestData$?.unsubscribe();
    this.quoteRequestState$?.unsubscribe();
  }

  ngOnInit(): void {
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      }
    });

    this.creditCheckButton$ = this.store.select(creditCheckViewModelSelector).subscribe((result) => {
      this.displayCreditCheckButton = result.isVisible;
    });
  }

  private load() {
    // Extract to contract-details componet
    this.load$ = forkJoin({
      quote: this.quoteRequestService.getSavedQuote(this.quoteRequestId),
      creditCheckDetails: this.creditCheckService.getCreditCheckDetails(this.quoteRequestId),
    }).subscribe((res) => {
      this.quoteRequestState$ = this.store.select(quoteRequestStateSelector).subscribe((state) => {
        this.quoteRequestState = state;
      });

      this.term$ = this.store.select(termOfOfferSelector).subscribe((term) => {
        this.term = term;
      }); 

      this.loggedInUtilityHubIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
        this.uhUser = user;
        let state = this.quoteRequestState?.state?.toString();
        // Removes whitespace from quoteRequestType
        let quoteRequestType = this.quoteRequestState?.quoteRequestType.replace(/\s+/g, '') as QuoteRequestType;

        let zipCode = this.quoteRequestState?.zipCode;
        let commodity = this.quoteRequestState?.commodity;

        this.creditCheckDetails = res.creditCheckDetails;
        this.formatApprovedDate = formatDate(res.creditCheckDetails.approvedDate, 'MM/dd/yyyy', 'en-US');

        this.creditCheckStatus = res.creditCheckDetails.status;
        this.isCreditCheckApproved = res.creditCheckDetails.isApproved;

        this.store.dispatch(new SetQuote(res.quote, user, this.quoteRequestState?.hasIDontKnows));
        this.store.dispatch(new SetUtilityInformation(this.quoteRequestId.toString(), state, quoteRequestType, zipCode, commodity));
        this.store.dispatch(new SetCreditCheckStatus(this.quoteRequestId, this.creditCheckStatus));
        this.store.dispatch(new SetIsCreditCheckApproved(this.quoteRequestId, this.isCreditCheckApproved));
      });

      this.store.dispatch(new LoadConsumptionData(this.quoteRequestId.toString(), this.term, res.quote.upliftDollar));

      this.creditCheckRequestData$ = this.store.select(creditCheckRequestDataSelector).subscribe((requestData) => {

        this.creditCheckRequestData = {
          businessName: requestData.businessName,
          creditLimit: requestData.creditLimit,
          dunsNumber: requestData.dunsNumber,
          quoteRequestId: this.quoteRequestId,
          amountThresholdApproval: requestData.amountThresholdApproval
        };
      });
    });
  }

  public performCreditCheck(): void {
    this.isCreditCheckInProgress = true;

    this.performCreditCheck$ = this.creditCheckService.performCreditCheck(this.creditCheckRequestData).subscribe((result) => {
      this.creditCheckStatus = result.status;
      this.creditCheckDetails.requestDate = result.requestDate;
      this.creditCheckDetails.errorMessage = result.errorMessage;

      this.store.dispatch(new SetCreditCheckStatus(this.quoteRequestId, this.creditCheckStatus));

      this.isCreditCheckInProgress = false;
    },
      (error: HttpErrorResponse) => {

        this.creditCheckStatus = CreditCheckStatus.CreditError;
        if (error.status == 400)
          this.creditCheckDetails.errorMessage = error.error;
        else
          this.creditCheckDetails.errorMessage = "Unknown error occured";

        this.isCreditCheckInProgress = false;
      });
  }

  public approveCreditCheck(): void {
    this.isApproveCreditCheckInProgress = true;

    this.creditCheckService.approveCreditCheck(this.quoteRequestId).subscribe((result) => {
      this.isCreditCheckApproved = true;
      this.creditCheckDetails.approvedBy = this.uhUser.fullName;
      this.formatApprovedDate = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');
      this.store.dispatch(new SetIsCreditCheckApproved(this.quoteRequestId, this.isCreditCheckApproved));

      this.isApproveCreditCheckInProgress = false;
    },
    (error) => {
      this.isApproveCreditCheckInProgress = false;
    });
  }
}
